/** Copyright 2024 SAP SE or an SAP affiliate company. All rights reserved. */
import { GetTasksResponseDto, UserDto } from '@/commons/dtos';
import { ControlTypeDtoSchema, SelectIntegrationTaskSchema } from '@/commons/schemas';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { useTranslations } from '@/hooks/useTranslations';
import { freApi } from '@/store/apis';
import { actions } from '@/store/Products';
import { isJSONObject } from '@/utils/utils';
import { Card, ExpandableText, FlexBox, Label, Link, MessageStrip, Tag, TagPropTypes, Text, Title } from '@ui5/webcomponents-react';
import { ThemingParameters } from '@ui5/webcomponents-react-base';
import { useEffect, useMemo } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { z } from 'zod';

const EXCLUDED_TASK_TYPE = ['reviewLayout', 'Video', 'IllustrationImage'];
const OTHER_KEYS = ['Other (Please specify):', 'Certificates not listed here'];
const SELECT_INTEGRATION = 'SELECT_INTEGRATION';

type Phase = {
    id: string;
    name: string;
    steps: Step[];
    order: number;
    completed: boolean;
    titleKey: string;
};

type Step = {
    id: string;
    name: string;
    tasks: Task[];
    order: number;
    titleKey: string;
};

type Task = {
    id: string;
    titleKey: string;
    answer: TaskAnswer;
    order: number;
    required: boolean;
    answerKey?: string;
    controlType?: z.infer<typeof ControlTypeDtoSchema> | null;
};

type TaskAnswer = {
    value: string | string[];
    completed: boolean;
};

export function getPhases({
    taskQuery,
    users,
    isCommodityCodeExist,
}: {
    taskQuery: GetTasksResponseDto;
    users: UserDto[];
    isCommodityCodeExist: boolean;
}): Phase[] {
    const { tasks, values } = taskQuery;
    const phases: Phase[] = [];

    tasks.forEach(t => {
        if (t.controlType?.name && EXCLUDED_TASK_TYPE.includes(t.controlType?.name)) return;

        const phase = phases.find(p => p.id === t.phase.id);
        if (!phase) {
            phases.push({
                id: t.phase.id,
                name: t.phase.name,
                steps: [],
                order: t.phase.displayOrder,
                completed: true,
                titleKey: t.phase.translationNameKey,
            });
        }
    });

    tasks.forEach(t => {
        if (t.controlType?.name && EXCLUDED_TASK_TYPE.includes(t.controlType?.name)) return;

        const phase = phases.find(p => p.id === t.phase.id)!;
        const step = phase.steps.find(s => s.id === t.step.id);
        if (!step) {
            phase.steps.push({
                id: t.step.id,
                name: t.step.name,
                tasks: [],
                order: t.step.displayOrder,
                titleKey: t.step.translationTitleKey,
            });
        }
    });

    tasks.forEach(t => {
        if (t.controlType?.name && EXCLUDED_TASK_TYPE.includes(t.controlType?.name)) return;

        const phase = phases.find(p => p.id === t.phase.id)!;
        const step = phase.steps.find(s => s.id === t.step.id)!;
        const answer = getTaskAnswer({
            configurationValue: values[t.productConfigurationKey || '']?.configurationValue ?? '',
            users,
            controlType: t.controlType?.name ?? '',
            isCommodityCodeExist,
            titleKey: t.translationTitleKey,
        });

        step.tasks.push({
            id: t.id,
            titleKey: t.translationTitleKey,
            answer,
            order: t.displayOrder,
            required: t.mandatory,
            answerKey: t.controlType?.controlTypeOptions.find(option => option.value === answer.value)?.translationValueKey ?? '',
            controlType: t.controlType,
        });
    });

    // Sort phases, steps and tasks
    phases.sort((a, b) => a.order - b.order);
    phases.forEach(p => {
        p.steps.sort((a, b) => a.order - b.order);
        p.steps.forEach(s => s.tasks.sort((a, b) => a.order - b.order));
    });

    // Determine if phase is completed
    phases.forEach(p => {
        p.steps.forEach(s => {
            s.tasks.forEach(t => {
                if (!t.answer.completed && t.required) {
                    p.completed = false;
                }
            });
        });
    });

    return phases;
}

export function getTaskAnswer({
    configurationValue,
    users,
    controlType,
    isCommodityCodeExist,
    titleKey,
}: {
    configurationValue: string;
    users: UserDto[];
    controlType: string;
    isCommodityCodeExist: boolean;
    titleKey: string;
}): TaskAnswer {
    if (['CompanyInformation', 'SuccessMeasures'].includes(controlType)) {
        const parsedAnswer = Object.values(JSON.parse(configurationValue || '{}') as Record<string, string>);
        return { value: parsedAnswer, completed: parsedAnswer.length ? parsedAnswer.every(a => !!a) : false };
    }

    if (controlType === 'AdditionalUser') {
        return {
            value: users.map(u => JSON.stringify(u)),
            completed: !!users.length,
        };
    }

    if (controlType === 'TableLayout' && isCommodityCodeExist) {
        return { value: 'CODE_UPLOADED', completed: true };
    }

    if (controlType === 'checkbox' && isJSONObject(configurationValue)) {
        const parsedAnswer = JSON.parse(configurationValue) as {
            values?: string[];
            others?: string;
        };

        if (!parsedAnswer.values) return { value: [], completed: false };

        const otherKey = parsedAnswer.values.find(value => OTHER_KEYS.includes(value));

        if (otherKey && parsedAnswer.others) {
            const index = parsedAnswer.values.indexOf(otherKey);
            parsedAnswer.values[index] = `${otherKey} ${parsedAnswer.others}`;
        }

        return { value: parsedAnswer.values, completed: parsedAnswer.values.length > 0 };
    }
    const isSystemIntegrationTask = titleKey === SELECT_INTEGRATION;
    if (isSystemIntegrationTask && isJSONObject(configurationValue)) {
        let isCompleted = false;
        const parsedAnswer = SelectIntegrationTaskSchema.safeParse(JSON.parse(configurationValue));
        if (parsedAnswer.success) {
            if (
                parsedAnswer.data?.buyerAccountSBN?.id &&
                parsedAnswer.data?.s4HanaPC?.id &&
                parsedAnswer.data?.buyerAccountSBN?.businessTypeId &&
                parsedAnswer.data?.s4HanaPC?.businessTypeId
            ) {
                isCompleted = true;
            }
        }
        return { value: configurationValue, completed: isCompleted };
    }

    return { value: configurationValue, completed: !!configurationValue };
}

Review.useReviewData = (currentRunId: string) => {
    const { flowId } = useParams();
    const { data: users } = freApi.useGetUsersQuery(currentRunId, {
        skip: !currentRunId,
    });
    const { data: taskQuery, error: taskQueryError } = freApi.useGetTasksQuery(
        {
            flowId: flowId!,
            runId: currentRunId,
        },
        {
            skip: !currentRunId || !flowId,
            refetchOnMountOrArgChange: true,
        }
    );
    const { data: isCommodityCodeExist } = freApi.useGetCommodityCodeRunExistQuery(currentRunId, {
        skip: !currentRunId,
    });

    const phases = useMemo(() => {
        if (!taskQuery || !users || isCommodityCodeExist === undefined) return [];

        return getPhases({ taskQuery, users, isCommodityCodeExist });
    }, [taskQuery, users, isCommodityCodeExist]);

    return { phases, taskQueryError };
};

Review.useOnPhasesLoaded = function (phases: Phase[]) {
    const dispatch = useAppDispatch();

    useEffect(() => {
        if (!phases.length) return;

        let ableToLaunch = true;

        phases.forEach(p => {
            if (p.completed) return;

            if (p.titleKey === 'PARTNERS') return;

            p.steps.forEach(s => {
                s.tasks.forEach(t => {
                    if (t.required && !t.answer.completed) {
                        ableToLaunch = false;
                    }
                });
            });
        });

        dispatch(actions.setUnansweredQuestions(!ableToLaunch));
    }, [phases]);
};

export default function Review() {
    const { t } = useTranslations();
    const productList = useAppSelector(state => state.productList);
    const { taskQueryError, phases } = Review.useReviewData(productList.currentRunId);
    Review.useOnPhasesLoaded(phases);

    if (taskQueryError) {
        console.error('Error loading taskQuery:', taskQueryError);
        return <Navigate to="/" />;
    }

    if (!phases.length) {
        return null;
    }

    return (
        <FlexBox direction="Column">
            <Card>
                <FlexBox
                    direction="Column"
                    gap={ThemingParameters.sapContent_Padding_M}
                    style={{
                        padding: `${ThemingParameters.sapContent_Padding_M} 0`,
                    }}
                    alignItems="Center"
                >
                    <Title style={{ fontSize: ThemingParameters.sapFontHeader3Size }} level="H3">
                        {t('REVIEW')}
                    </Title>

                    <div
                        style={{
                            width: '100%',
                            padding: '0 1rem',
                        }}
                    >
                        {phases.map((phase, i) => (
                            <Review.PhaseItem key={phase.id} data={phase} order={i + 1} isEditable />
                        ))}
                    </div>
                </FlexBox>
            </Card>
            {productList.unansweredQuestions && (
                <MessageStrip design="Information" hideCloseButton style={{ marginTop: ThemingParameters.sapContent_Space_Small }}>
                    {t('REVIEW_WARNING_TEXT')}
                </MessageStrip>
            )}
        </FlexBox>
    );
}

Review.PhaseItem = function PhaseItem({ data, order, isEditable = true }: { data: Phase; order: number; isEditable: boolean }) {
    const { t } = useTranslations();
    const status = useMemo(() => {
        if (data.completed) return 'complete';

        // Special case for Partners phase - show as incomplete-optional instead of error
        if (data.titleKey === 'PARTNERS' || t(data.titleKey) === 'Partners') {
            return 'incomplete-optional';
        }

        if (data.steps.some(step => step.tasks.some(task => task.required && !task.answer.completed))) {
            return 'incomplete-mandatory';
        }
        return 'incomplete-optional';
    }, []);

    return (
        <div data-testid={`phase-item-${data.id}`}>
            <FlexBox
                justifyContent="SpaceBetween"
                style={{
                    borderTop: `1px solid ${ThemingParameters.sapList_BorderColor}`,
                    borderBottom: `1px solid ${ThemingParameters.sapList_BorderColor}`,
                    padding: 16,
                }}
                alignItems="Center"
            >
                <Title
                    level="H6"
                    style={{
                        color: ThemingParameters.sapPageHeader_TextColor,
                        fontWeight: '700',
                    }}
                >
                    {order}. {t({ key: data.titleKey, defaultText: data.name })}
                </Title>

                {isEditable && <Review.StatusBadge status={status} />}
            </FlexBox>

            <FlexBox
                direction="Column"
                gap={16}
                style={{
                    padding: 18,
                }}
            >
                {data.steps.map(step => (
                    <Review.StepItem key={step.id} data={step} phaseId={data.id} isEditable={isEditable} />
                ))}
            </FlexBox>
        </div>
    );
};

Review.StatusBadge = function StatusBadge({ status }: { status: 'complete' | 'incomplete-mandatory' | 'incomplete-optional' }) {
    const { t } = useTranslations();

    const design = useMemo<TagPropTypes['design']>(() => {
        if (status === 'complete') return 'Positive';
        return status === 'incomplete-mandatory' ? 'Negative' : 'Critical';
    }, [status]);

    let statusText;
    if (status === 'complete') {
        statusText = t('COMPLETE');
    } else if (status === 'incomplete-mandatory') {
        statusText = t('ERROR');
    } else {
        statusText = t('INCOMPLETE');
    }
    return <Tag design={design}>{statusText}</Tag>;
};

Review.StepItem = function StepItem({ data, phaseId, isEditable }: { data: Step; phaseId: string; isEditable: boolean }) {
    const { t } = useTranslations();
    const { flowId } = useParams();

    return (
        <FlexBox justifyContent="SpaceBetween" alignItems="Start">
            <FlexBox gap={8}>
                <Label>{t({ key: data.titleKey, defaultText: data.name })}:</Label>
                {data.tasks.map(task => (
                    <Review.TaskItem key={task.id} task={task} />
                ))}
            </FlexBox>

            {isEditable && (
                <Link
                    style={{
                        color: ThemingParameters.sapButton_Lite_TextColor,
                        textDecoration: 'none',
                    }}
                    href={`/dashboard/flow/${flowId}/phase/${phaseId}/step/${data.id}`}
                >
                    {t('EDIT_BUTTON')}
                </Link>
            )}
        </FlexBox>
    );
};

Review.TaskItem = function TaskItem({ task }: { task: Task }) {
    const { t } = useTranslations();
    const isCommodityCodeTask = task.titleKey === 'COMMODITY_CODES';
    const isSystemIntegrationTask = task.titleKey === SELECT_INTEGRATION;
    const isSourcingProjectTask = task.titleKey === 'SPECIFY_TYPES_OF_PROJECTS_USERS_CAN_CREATE';

    if (Array.isArray(task.answer.value)) {
        const isEmpty = task.answer.value.every(selected => !selected);
        if (isEmpty) {
            return <>-</>;
        }

        if (task.controlType?.name === 'AdditionalUser') {
            const parsed = task.answer.value.map(user => JSON.parse(user) as UserDto);
            return (
                <div>
                    {parsed.map((user, i) => (
                        <Text key={i}>
                            {i + 1}. {t('USER_NAME_LABEL')}: {t(user.userName)}, {t('NAME')}: {`${user.familyName} ${user.givenName}`}, {t('EMAIL')}:{' '}
                            {user.emailId}
                        </Text>
                    ))}
                </div>
            );
        }

        return (
            <FlexBox direction="Column">
                {task.answer.value.map((selected, i) => {
                    const translationKey = task.controlType?.controlTypeOptions.find(option => option.value === selected)?.translationValueKey;
                    const getText = (selected: string, optionText: string, translationKey?: string | null) => {
                        if (translationKey) {
                            return t({ key: translationKey, defaultText: optionText });
                        } else if (selected) {
                            return t({ key: selected, defaultText: optionText });
                        } else {
                            return '-';
                        }
                    };

                    const optionText = task.controlType?.controlTypeOptions[i]?.value;
                    const text = getText(selected, optionText ?? '', translationKey);

                    return (
                        <div key={`${i + 1}. ${selected || '-'}`}>
                            <ExpandableText
                                data-testid="expandable-text"
                                maxCharacters={70}
                                overflowMode="Popover"
                            >{`${i + 1}. ${text}`}</ExpandableText>
                        </div>
                    );
                })}
            </FlexBox>
        );
    }

    // Only CommodityCodeTask need to be translated
    if (isCommodityCodeTask && task.answer.value) {
        return <Text>{t(task.answer.value)}</Text>;
    }

    if (isSourcingProjectTask) {
        const sourcingProjectValues = [
            {
                key: '0',
                value: t('BOTH_QUICK_AND_FULL_PROJECTS'),
            },
            {
                key: '1',
                value: t('FULL_PROJECTS_ONLY'),
            },
            {
                key: '2',
                value: t('QUICK_PROJECTS_ONLY'),
            },
            {
                key: '3',
                value: t('BOTH_QUICK_AND_FULL_PROJECTS_DEFAULT_TO_FULL'),
            },
        ];
        const selected = sourcingProjectValues.find(value => value.key === task.answer.value);
        return <Text>{selected ? selected.value : '-'}</Text>;
    }

    // Only SystemIntegrationTask need to be parsed and displayed by format of buyerAccountSBN.id + s4HanaPC.id
    if (isSystemIntegrationTask && isJSONObject(task.answer.value)) {
        const parsedAnswer = SelectIntegrationTaskSchema.safeParse(JSON.parse(task.answer.value));

        if (!parsedAnswer.success) {
            console.error('Error parsing system integration task answer:', parsedAnswer.error.errors);
            return <>-</>;
        }

        return (
            <Text>
                {parsedAnswer.data.buyerAccountSBN.id} + {parsedAnswer.data.s4HanaPC.id}
            </Text>
        );
    }

    if (isJSONObject(task.answer.value)) {
        const parsed = JSON.parse(task.answer.value) as Record<string, string>;

        return (
            <FlexBox direction="Column">
                {Object.entries(parsed).map(([key, value]) => (
                    <Text key={key}>
                        {t(key)}: {value}
                    </Text>
                ))}
            </FlexBox>
        );
    }

    if (task.answerKey) {
        return <Text>{t(task.answerKey)}</Text>;
    }

    const isYesNoAnswer = ['Yes', 'No'].includes(task.answer.value);
    if (isYesNoAnswer) {
        return <Text>{t(task.answer.value.toUpperCase())}</Text>;
    }

    // Other tasks are displayed as is
    return <Text>{task.answer.value || '-'}</Text>;
};
